import { useMutation } from '@apollo/client'
import { ReactElement, useEffect } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate, useParams } from 'react-router-dom'

import { DEFAULT_COUNTRY_CODE } from 'components/Formik/InputPhone/country-codes-list'
import FormLayout from 'components/v2/FormLayout'
import Heading from 'components/v2/Heading'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { NewBusinessClientUser } from 'features/BusinessClients/components/NewBusinessClientUser'
import { useLogin } from 'features/Login/hooks'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { canCreateBusinessClientUser } from 'features/Users/utils'
import { INewBusinessClientUserFormValues } from 'features/BusinessClients/types'
import { CREATE_BUSINESS_CLIENT_USER } from 'features/BusinessClients/mutations'
import { useBusinessClientAutocomplete } from 'features/BusinessClients/hooks'

const messages = defineMessages({
  helmet: {
    id: 'companyClients.new.helmet',
    defaultMessage: 'New Company Client | Nexbus Admin'
  }
})

const NewBusinessClientUserView = (): ReactElement => {
  const intl = useIntl()
  const login = useLogin()
  const { businessClientId } = useParams<{ businessClientId: string }>()
  const navigate = useNavigate()
  const [createBusinessClientUser, { loading, error }] = useMutation(CREATE_BUSINESS_CLIENT_USER)
  const businessClientAutocomplete = useBusinessClientAutocomplete(businessClientId as string, {
    skip: !businessClientId
  })
  const handleSubmit = async (values: INewBusinessClientUserFormValues): Promise<void> => {
    try {
      await createBusinessClientUser({
        variables: {
          input: {
            businessClientID: businessClientId as string,
            businessSiteID: values.businessSite?.id || null,
            firstName: values.firstName,
            lastName: values.lastName,
            hasAdminRole: values.hasAdminRole,
            phoneNumber: {
              countryCode: DEFAULT_COUNTRY_CODE,
              number: values.phone
            },
            email: values.email,
            title: values.title
          }
        }
      })
      navigate(`/business-clients/${businessClientId}/users`)
    } catch (error) {
      //do nothing
    }
  }

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canCreateBusinessClientUser(login.accessToken.policies)) {
    return <ForbiddenView feature={FEATURES.BUSINESS_CLIENT_USER} />
  }

  return (
    <FormLayout.Container>
      <FormLayout.Content>
        <Heading.H3>
          <FormattedMessage id="businessClient.user.new.title" defaultMessage="Add User" />
        </Heading.H3>
        <NewBusinessClientUser
          onSubmit={handleSubmit}
          businessClientId={businessClientId as string}
          businessClientAutocomplete={businessClientAutocomplete || undefined}
          loading={loading}
          error={error}
        />
      </FormLayout.Content>
    </FormLayout.Container>
  )
}

export default withLastUrl(NewBusinessClientUserView)
